import { DetermineUpdateServiceContractBody, SwitchProductParams, SwitchProductReturn } from './types.ts'
import { UserTypes } from 'store/auth/types.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { calculateInstalment } from 'utils/instalment.ts'
import { isActiveContract } from 'utils/contracts.ts'
import { roundToTwoDecimals } from 'utils/number.ts'
import {
  ENERGY_TYPES,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCT_SWITCH_INSTALMENT_TYPES,
  PRODUCTS_CONFIG,
  ProductSwitchRequestBody,
  SERVICE_CONTRACT_UPDATED_NOTIFICATION_TYPES
} from '@boltenergy-be/shared'
import backendLib from './index.ts'
import { updateServiceContract } from './resources/service-contracts/updateServiceContract.ts'

/**
 * Determines the smart meter option for updating & service contract
 *
 * @param {boolean | undefined} currentState
 * @param {boolean} newState
 * @param {boolean} eligible
 * @returns {undefined | boolean}
 */
const determineSmartMeterOptionForUpdateServiceContract = (
  currentState: boolean | undefined,
  newState: boolean,
  eligible: boolean
): boolean | undefined => {
  if (!eligible) return undefined
  if (typeof currentState === 'undefined' && !newState) return undefined
  if (typeof currentState === 'boolean' && currentState === newState) return undefined
  return newState
}

/**
 * Determines the body for update service contract request based on the product switch inputs, eligibility and current service contract details
 *
 * @param {DetermineUpdateServiceContractBody} param0
 * @returns {ProductSwitchRequestBody}
 */
const determineUpdateServiceContractBody = ({
  eligibility,
  inputs,
  newInstalment,
  product,
  serviceContract
}: DetermineUpdateServiceContractBody): ProductSwitchRequestBody => {
  return {
    detail: {
      dynamicTariff: determineSmartMeterOptionForUpdateServiceContract(
        serviceContract.detail.dynamicTariff,
        inputs.smartMeterOptions.includes('dynamicTariff'),
        eligibility?.dynamicTariff
      ),
      instalment: inputs.instalmentType === PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount ? newInstalment : undefined,
      monthlyBilling: determineSmartMeterOptionForUpdateServiceContract(
        serviceContract.detail.monthlyBilling,
        inputs.smartMeterOptions.includes('monthlyBilling'),
        eligibility?.monthlyBilling
      ),
      productCode: PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.productCodes][serviceContract.detail.energyType]
    },
    extraOptionsForProductSwitch: {
      newContractualPeriodStartDate: inputs.startDate,
      instalmentType: inputs.instalmentType,
      referenceDate: inputs.referenceDate
    }
  }
}

/**
 * Executes a product switch for a given billing contract & data
 * - Calls the updateServiceContract for each subordinate service contract
 * - Triggers the updatedServiceContracts notification
 *
 * @param {SwitchProductParams} param0
 * @returns {Promise<SwitchProductReturn>}
 */
const switchProduct = async ({
  billingContract,
  eligibility,
  inputs,
  product,
  userType
}: SwitchProductParams): Promise<SwitchProductReturn> => {
  // Check if user can switch the product
  if (!userType || userType === UserTypes.SALES) {
    log({ error: `Cannot switch product as ${userType}`, identifier: `[${Flow.TARIFFS}:switchProduct:unauthorized]` })
    return { success: false }
  }

  try {
    // 1. CALCULATE NEW INSTALMENT

    let newElectricityInstalment = 0
    let newGasInstalment = 0

    if (inputs.instalmentType === PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount && inputs.newTotalInstalmentAmount) {
      const currentTotalInstalment =
        (billingContract.serviceContracts.electricity.detail.instalment || 0) +
        (billingContract.serviceContracts.gas?.detail?.instalment || 0)
      const newTotalInstalment = inputs.newTotalInstalmentAmount

      const { ratio } = calculateInstalment({
        userType,
        initialMinInstalment: 0,
        initialMaxSliderBoundary: 0,
        hasEffectiveGasContract: !!billingContract.serviceContracts.gas && isActiveContract(billingContract.serviceContracts.gas),
        currentInstalment: {
          total: currentTotalInstalment,
          electricity: billingContract.serviceContracts.electricity.detail.instalment || 0,
          gas: billingContract.serviceContracts.gas?.detail?.instalment || 0
        }
      })

      newElectricityInstalment = ratio.electricity === 0 ? 10 : roundToTwoDecimals(newTotalInstalment * ratio.electricity)
      newGasInstalment = ratio.electricity === 0 ? newTotalInstalment - 10 : roundToTwoDecimals(newTotalInstalment * ratio.gas)
    }

    // 2. UPDATE SERVICE CONTRACTS

    const [electricityResponse, gasResponse] = await Promise.all([
      updateServiceContract(
        billingContract.serviceContracts.electricity.contractNumber,
        determineUpdateServiceContractBody({
          eligibility: eligibility[ENERGY_TYPES.electricity],
          inputs,
          newInstalment: newElectricityInstalment,
          product,
          serviceContract: billingContract.serviceContracts.electricity
        })
      ),
      ...(billingContract.serviceContracts.gas
        ? [
            updateServiceContract(
              billingContract.serviceContracts.gas.contractNumber,
              determineUpdateServiceContractBody({
                eligibility: {
                  dynamicTariff: false, // Gas contracts can not have dynamic tariff
                  monthlyBilling: eligibility[ENERGY_TYPES.gas]?.monthlyBilling || false
                },
                inputs,
                newInstalment: newGasInstalment,
                product,
                serviceContract: billingContract.serviceContracts.gas
              })
            )
          ]
        : [])
    ])

    // 3. TRIGGER NOTIFICATIONS (IF SUCCESS) & RETURN

    if (electricityResponse && electricityResponse.success && electricityResponse.data?.contract) {
      await backendLib.notifyServiceContractsUpdated(billingContract.contractNumber, {
        notificationType: SERVICE_CONTRACT_UPDATED_NOTIFICATION_TYPES.productSwitch
      })

      return {
        [ENERGY_TYPES.electricity]: electricityResponse.data.contract,
        [ENERGY_TYPES.gas]: gasResponse ? gasResponse.data?.contract : undefined
      }
    }

    log({
      error: `Something went wrong with the product switch.`,
      identifier: `[${Flow.TARIFFS}:switchProduct]`,
      metaData: {
        switchProductError: {
          electricityResponse,
          gasResponse
        }
      }
    })

    return { success: false, errorCode: electricityResponse.errorCode || gasResponse?.errorCode }
  } catch (error) {
    log({
      error: error instanceof Error ? error : 'Unknown error in product switch',
      identifier: `[${Flow.TARIFFS}:CATCH:switchProduct]`
    })

    return { success: false }
  }
}

export default {
  switchProduct
}
